import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Shell from "../components/Shell";
import { api, SUBJECTS, subjectMeta, errMsg, getRole } from "../lib/api";
import { celebrateAwards } from "../lib/celebrate";
import { CameraPlus, X, ArrowRight, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result); // data:image/...;base64,XXXX
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const downscale = (dataUrl, maxDim = 1600, quality = 0.85) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });

const LAST_SUBJECT_KEY = "evodevi_last_subject";

export default function Upload() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const initial = params.get("subject") || (typeof window !== "undefined" ? localStorage.getItem(LAST_SUBJECT_KEY) : "") || "";
  const [subject, setSubject] = useState(initial);
  const [photos, setPhotos] = useState([]); // [{ dataUrl }]
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const chooseSubject = (key) => {
    setSubject(key);
    try { localStorage.setItem(LAST_SUBJECT_KEY, key); } catch (_) {}
  };
  const isParent = getRole() === "parent";

  const addFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = 6 - photos.length;
    const list = Array.from(files).slice(0, remaining);
    if (list.length < files.length) toast.warning("En fazla 6 fotoğraf ekleyebilirsin.");
    const out = [];
    for (const f of list) {
      if (!/image\/(jpeg|jpg|png|webp)/i.test(f.type)) {
        toast.error(`${f.name}: sadece JPG/PNG/WEBP destekleniyor.`);
        continue;
      }
      const raw = await fileToBase64(f);
      const small = await downscale(raw);
      out.push({ dataUrl: small });
    }
    setPhotos((p) => [...p, ...out]);
  };

  const onAnalyze = async () => {
    if (!subject) { toast.error("Önce bir ders seç."); return; }
    if (photos.length === 0) { toast.error("En az 1 fotoğraf ekle."); return; }
    setBusy(true);
    try {
      const images_base64 = photos.map((p) => p.dataUrl);
      const { data } = await api.post("/submissions", { subject, images_base64 }, { timeout: 180000 });
      toast.success("Analiz tamam!");
      celebrateAwards(data.awards);
      const role = getRole();
      nav(role === "parent" ? `/parent/result/${data.id}` : `/app/result/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e, "Analiz başarısız oldu."));
    } finally {
      setBusy(false);
    }
  };

  if (busy) {
    return (
      <Shell title="Analiz ediliyor..." hideNav>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4 text-center">
          <div className="w-24 h-24 rounded-full bg-brand-primary/30 flex items-center justify-center animate-float">
            <div className="w-14 h-14 rounded-full bg-brand-primary flex items-center justify-center">
              <ImageSquare size={28} weight="duotone" color="#1D3557" />
            </div>
          </div>
          <div>
            <div className="font-outfit text-2xl font-extrabold text-brand-ink">Ödevini okuyorum 👀</div>
            <div className="font-nunito text-brand-ink2 mt-2">Soruları sayıyor, cevaplarını kontrol ediyorum...</div>
            <div className="font-nunito text-xs text-brand-ink2/70 mt-3">Bu işlem 20-60 saniye sürebilir.</div>
          </div>
          <div className="flex gap-1 mt-2">
            {[0,1,2].map((i) => <span key={i} className="w-2.5 h-2.5 rounded-full bg-brand-ink/30 animate-pulse" style={{ animationDelay: `${i*0.2}s` }} />)}
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title="Yeni ödev yükle" hideNav={isParent}>
      <div className="mb-5">
        <div className="text-xs uppercase tracking-widest font-nunito font-semibold text-brand-ink2 mb-2 flex items-center justify-between">
          <span>Ders {!subject && <span className="text-bad normal-case tracking-normal font-bold ml-1">· gerekli</span>}</span>
          <span className="text-brand-ink2/70 font-normal normal-case tracking-normal">Kaydır →</span>
        </div>
        <div className="relative">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scroll-smooth" data-testid="subject-picker">
            {SUBJECTS.map((s) => {
              const selected = subject === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`pick-subject-${s.key}`}
                  onClick={() => chooseSubject(s.key)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-full font-nunito font-bold text-sm border-2 transition-all ${
                    selected ? "border-brand-ink shadow-md scale-[1.04]" : "border-transparent opacity-80"
                  }`}
                  style={{ backgroundColor: s.bg, color: s.color }}
                >
                  {selected && "✓ "}{s.name}
                </button>
              );
            })}
          </div>
          {/* right fade to hint scrollability */}
          <div className="pointer-events-none absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-brand-paper to-transparent" />
        </div>
      </div>

      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest font-nunito font-semibold text-brand-ink2">
            Fotoğraflar ({photos.length}/6)
          </div>
          {photos.length > 0 && (
            <button onClick={() => setPhotos([])} className="text-xs font-nunito font-semibold text-bad" data-testid="clear-photos-button">
              Hepsini sil
            </button>
          )}
        </div>

        {photos.length === 0 ? (
          <button
            data-testid="upload-zone"
            onClick={() => fileRef.current?.click()}
            className="w-full aspect-[3/4] bg-white border-2 border-dashed border-brand-secondary rounded-3xl flex flex-col items-center justify-center gap-3 text-brand-ink2"
          >
            <CameraPlus size={56} weight="duotone" color="#FFB703" />
            <div className="font-outfit font-bold text-lg text-brand-ink">Fotoğraf ekle</div>
            <div className="font-nunito text-sm">Birden fazla sayfayı tek seferde yükleyebilirsin.</div>
          </button>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative aspect-square bg-brand-paperAlt rounded-2xl overflow-hidden">
                <img src={p.dataUrl} alt="" className="w-full h-full object-cover" />
                <button
                  data-testid={`remove-photo-${i}`}
                  onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                  className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            ))}
            {photos.length < 6 && (
              <button
                data-testid="add-more-photos"
                onClick={() => fileRef.current?.click()}
                className="aspect-square bg-white border-2 border-dashed border-brand-secondary rounded-2xl flex items-center justify-center text-brand-ink2"
              >
                <CameraPlus size={28} weight="duotone" color="#FFB703" />
              </button>
            )}
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          capture="environment"
          className="hidden"
          data-testid="upload-photo-input"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      <button
        data-testid="analyze-homework-button"
        onClick={onAnalyze}
        className={`w-full bg-brand-primary text-brand-ink font-outfit font-bold text-lg py-4 rounded-full flex items-center justify-center gap-2 active:scale-[0.99] transition-transform ${
          (!subject || photos.length === 0)
            ? "opacity-60 shadow-none"
            : "shadow-[0_4px_18px_rgba(255,183,3,0.45)]"
        }`}
      >
        Analiz Et <ArrowRight size={20} weight="bold" />
      </button>
      {(!subject || photos.length === 0) && (
        <div className="mt-3 text-center text-sm font-nunito text-bad font-semibold" data-testid="analyze-hint">
          {!subject && photos.length === 0 && "Önce bir ders seç ve en az 1 fotoğraf ekle."}
          {!subject && photos.length > 0 && "Önce bir ders seç — sağa kaydırırsan tümünü görürsün."}
          {subject && photos.length === 0 && "En az 1 fotoğraf ekle."}
        </div>
      )}
      {subject && photos.length > 0 && (
        <div className="mt-3 text-center text-xs font-nunito text-brand-ink2">
          Seçilen ders: <b style={{ color: subjectMeta(subject).color }}>{subjectMeta(subject).name}</b>
        </div>
      )}
    </Shell>
  );
}
